/*#
# code

A [CodeMirror 6](https://codemirror.net/) wrapper.

Sometimes, it's nice to be able to just toss a code-editor in a web-page.

`<tosi-code>`'s `value` is the code it contains. Its `mode` attribute sets the
language (`javascript`, `typescript`, `tjs`, `ajs`, `css`, `html`, `markdown`).

```html
<tosi-code style="width: 100%; height: 100%" mode="css">
body {
  box-sizing: border-box;
}
</tosi-code>
```

## Properties & events

| member | what it does |
| --- | --- |
| `value` | the code in the editor (get/set) |
| `mode` | `javascript`, `typescript`, `tjs`, `ajs`, `css`, `html`, `markdown` |
| `disabled` | makes the editor read-only |
| `original` + `showDiff(on)` | diff the current `value` against a baseline, as an overlay |
| `diffResolvable` | make that overlay **resolvable**: each change gets keep/revert buttons, and the choices are applied to `value` when the diff closes |
| `diffOriginalLabel` / `diffModifiedLabel` | what the two buttons say (default `Original`/`Modified`) |
| `editor` | the underlying CodeMirror [`EditorView`](https://codemirror.net/docs/ref/#view.EditorView) (`undefined` until loaded) — see **Extending the editor** below |
| `undo()` / `redo()` / `canUndo()` / `canRedo()` | history control |
| `tjsAutocomplete` | runtime-value autocomplete hooks (tjs mode) — see below |
| `change` event | fires when the text changes; `event.detail.value` is the new text |

In `tjs`/`ajs` mode the editor loads tjs-lang's CodeMirror language and completion
source (if `tjs-lang` is installed — it's an optional peer). Set `tjsAutocomplete`
to a `TjsAutocompleteConfig` and completion will suggest the **real members of live
runtime values** — including proxy members no static analysis can see:

```typescript
codeEl.tjsAutocomplete = { getLiveBindings: () => ({ app, elements }) }
```

## Selective revert

`showDiff(on)` shows a read-only diff. Set `diffResolvable` first and the overlay becomes a
review surface instead: every change carries keep/revert buttons, defaulting to **keep**, and
whatever you leave alone survives. This is what `<tosi-example>`'s **View changes** uses — you
tried four things and three worked, so revert the one you regret rather than all four.

The choices are applied when the diff **closes**, not as you click, and a `change` event fires
if the text moved. That is deliberate: writing each choice straight back into `value` would
feed the new text into the overlay's `modified`, and `<tosi-diff>` resets its decisions when
either input changes — so live application would wipe the choices making it and renumber the
hunks under the pointer.

```typescript
codeEl.original = savedSource
codeEl.diffResolvable = true
codeEl.diffOriginalLabel = 'Source'
codeEl.diffModifiedLabel = 'Yours'
codeEl.showDiff(true)
// …user reverts a hunk or two…
codeEl.showDiff(false)   // codeEl.value now reflects their choices
```

## Bundling

CodeMirror is a **lazy chunk**: with a bundler (ESM), a page that never uses
`<tosi-code>` doesn't load it. **This is not true of the IIFE** (`dist/iife.js`) —
bun's IIFE format cannot code-split, so CodeMirror is inlined there. That is a
deliberate trade: the doc-system's editor (and its save-to-source flow) is the
point of the IIFE, so it carries the editor; see the README for what the IIFE weighs.

## Migrating from the ACE editor (pre-1.7)

1.7 replaced ACE with CodeMirror 6. `value`, `original`/`showDiff()`, `mode` and
`disabled` are unchanged. Removed (each warns once, then no-ops):

| removed | replacement |
| --- | --- |
| `theme` | style with `--code-bg` / `--text-color` |
| `options` (ACE-shaped) | configure via `editor` (an `EditorView`) |
| `ace` | there is no ACE global; use `editor` |
| `editor.session.getUndoManager()` | `undo()` / `redo()` / `canUndo()` / `canRedo()` |

`editor` **changed type in place** — it was an ACE `Editor`, it is now a CodeMirror
`EditorView`. Code that reached into it needs revisiting; a grep for removed names
won't catch this one.

## Extending the editor

Import CodeMirror from **`tosijs-ui/codemirror`**, not from `@codemirror/*` directly:

```typescript
import { gutter, GutterMarker, StateField, StateEffect } from 'tosijs-ui/codemirror'

const view = codeEl.editor // undefined until the editor has loaded
view?.dispatch({ effects: StateEffect.appendConfig.of([myField, myGutter]) })
```

**This matters more than it looks.** CodeMirror 6 keys facets, `StateField`s and gutters by
**object identity**, not by module name. If your `@codemirror/view` resolves to a different
copy than the one `<tosi-code>` uses — which a package manager will happily arrange when the
versions don't dedupe — the view **silently ignores** your extension. No error, no warning;
the gutter simply never renders, and every reading of your code says it should.

The re-export removes that failure by construction: there is only ever one copy, because it is
the one this package resolves. Nothing to pin, no `overrides` to write.

Only `@codemirror/state` and `@codemirror/view` are re-exported — the two you need to extend a
view. The language, lint and search packages are internal composition details; ask if you need
one exposed.
*/

/*{ "parent": "Components", "headTitle": "<tosi-code> CodeMirror 6 code editor — tosijs-ui", "description": "<tosi-code> embeds a CodeMirror 6 editor for JavaScript, TypeScript, CSS, HTML and Markdown, with undo, a diff view and a change event." }*/

import {
  ElementCreator,
  elements,
  PartsMap,
  varDefault,
  withAttributes,
} from 'tosijs'
import { tosiDiff, TosiDiff } from './diff.js'
import type { CmHandle, TjsAutocompleteConfig } from './code-editor-cm.js'
export type { TjsAutocompleteConfig } from './code-editor-cm.js'

const { div } = elements

// Warn once per removed member, not once per access — a live-example page holds
// dozens of editors and a render-loop read would otherwise flood the console.
const warned = new Set<string>()
const warnRemoved = (member: string, advice: string): void => {
  if (warned.has(member)) return
  warned.add(member)
  console.warn(
    `<tosi-code>.${member} was removed in tosijs-ui 1.7 (the editor is now CodeMirror 6, not ACE) — ${advice}. See CHANGELOG 1.7.0.`
  )
}

interface CodeEditorParts extends PartsMap {
  host: HTMLDivElement
  diffHost: HTMLDivElement
}

// One warning per page, not per element — see the `editor` getter.
let warnedEditor = false
let warnedTjs = false

export class CodeEditor extends withAttributes({
  mode: 'javascript',
  disabled: false,
})<CodeEditorParts> {
  static preferredTagName = 'tosi-code'

  private source = ''
  private _handle: CmHandle | undefined
  private _loadPromise: Promise<CmHandle | undefined> | undefined
  // Bumped on every connect. The lazy chunk resolves asynchronously, so by the time it
  // does, this element may have been removed — or removed and re-added, starting a newer
  // load. Mounting a stale load would build an EditorView nothing ever destroys.
  private _loadGeneration = 0
  private _appliedMode = ''
  private _appliedDisabled: boolean | undefined
  private _tjsAutocomplete: TjsAutocompleteConfig | undefined

  /**
   * Runtime-introspection hooks for tjs autocomplete (`getLiveBindings` /
   * `getMembers`) — lets completion suggest the REAL members of live values (e.g.
   * a tosijs proxy or a DOM element) that static analysis can't see. Only used in
   * tjs mode; setting it re-applies the tjs extension so it takes effect live.
   */
  get tjsAutocomplete(): TjsAutocompleteConfig | undefined {
    return this._tjsAutocomplete
  }
  set tjsAutocomplete(config: TjsAutocompleteConfig | undefined) {
    this._tjsAutocomplete = config
    if (this._handle && this.isTjsMode()) this.applyTjsExtension()
  }

  get value(): string {
    return this._handle ? this._handle.getValue() : this.source
  }

  set value(text: string) {
    if (this._handle) {
      this._handle.setValue(text)
    } else {
      this.source = text
    }
  }

  // Baseline for `showDiff()` — the version to diff the current `value` against.
  // Defaults to the current value (no diff) until a caller sets it.
  private _original: string | undefined
  get original(): string {
    return this._original ?? this.value
  }
  set original(text: string) {
    this._original = text
  }

  // Diff overlay — built only on the editor's public surface (`value` + `original`)
  // and the tosi-diff component, never the underlying editor's API, so it stays
  // editor-agnostic.
  //
  // It lives in the SHADOW root (the `diffHost` part), not the light DOM. Under the
  // old Ace editor this component had no `content`, so tosijs's default `slot()`
  // filled the shadow root and Ace mounted into the light DOM — a light-DOM overlay
  // projected through that slot. CodeMirror mounts into `[part=host]` inside the
  // shadow root, and this component now declares its own `content`, so there is no
  // slot: `this.append(overlay)` would put it in the light DOM where nothing renders
  // it. (Re-adding a slot is NOT the fix — it would also project the element's
  // textContent, i.e. the initial code, and double-render it under the editor.)
  private diffOverlay: TosiDiff | undefined

  // Hydration state comes from the base class (`this.hydrated`), as of tosijs 1.6.9.
  // This used to be a hand-rolled `_partsHydrated` flag — because before 1.6.9 the
  // `parts` proxy poisoned itself on any pre-hydration read (touching it once rooted
  // the proxy at the light-DOM element forever, so the editor never mounted), and
  // there was no public way to ask "am I hydrated yet?" without triggering exactly
  // that. 1.6.9 fixed both — it invalidates the cached proxy at hydrate AND exposes
  // `hydrated`/`whenHydrated` — so the flag is gone. (tonioloewald/tosijs#13.)

  // A showDiff() call made before hydration, replayed once we're ready. Still needed:
  // `parts` genuinely doesn't exist before hydration (content is injected at connect),
  // so work that touches it is legitimately deferred — that is not the poisoning bug.
  private _pendingDiff: boolean | undefined

  get showingDiff(): boolean {
    if (!this.hydrated) return this._pendingDiff ?? false
    return !this.parts.diffHost.hidden
  }

  /*
  Make the diff overlay RESOLVABLE, turning it from a review into selective revert.

  Off by default, so `showDiff()` stays exactly what it was. With it on, every change gets
  keep/revert buttons and the ones you leave alone are kept — which is the useful default when
  the "modified" side is your own edit: you are reverting the two lines you regret, not
  re-accepting the twenty you meant.
  */
  diffResolvable = false
  diffOriginalLabel = 'Original'
  diffModifiedLabel = 'Modified'

  showDiff(on: boolean): void {
    if (!this.hydrated) {
      this._pendingDiff = on
      return
    }
    const { diffHost } = this.parts
    if (on) {
      if (this.diffOverlay === undefined) {
        this.diffOverlay = tosiDiff()
        diffHost.append(this.diffOverlay)
      }
      this.diffOverlay.resolvable = this.diffResolvable
      this.diffOverlay.originalLabel = this.diffOriginalLabel
      this.diffOverlay.modifiedLabel = this.diffModifiedLabel
      this.diffOverlay.original = this.original
      this.diffOverlay.modified = this.value
    } else if (this.diffOverlay !== undefined && this.diffResolvable) {
      /*
      Resolutions land on the way OUT, not as you click.

      Writing each choice straight back into `value` would feed the editor's new text into the
      overlay's `modified`, and `<tosi-diff>` resets its decisions when either input changes —
      by design, because choice #2 of the old diff is not choice #2 of the new one. Live
      application would therefore wipe the very choices making it, and the hunks would
      renumber under the pointer. Applying once, when the diff closes, sidesteps that
      completely and matches how a reviewer thinks: decide, then commit.

      The `change` event matters as much as the value: it is how a host recomputes "does this
      still differ from the source" after a partial revert.
      */
      const resolved = this.diffOverlay.value
      if (resolved !== this.value) {
        this.value = resolved
        this.dispatchEvent(
          new CustomEvent('change', { detail: { value: resolved } })
        )
      }
    }
    diffHost.hidden = !on
  }

  role = 'code editor'

  /**
   * The underlying CodeMirror `EditorView` (undefined until loaded).
   *
   * **Changed in 1.7 (ACE → CodeMirror 6).** In 1.6 this was an ACE editor, and
   * `editor.session.getUndoManager()` was documented public API. `^1.6.x` resolves
   * 1.7.0, so an app that never changed a line auto-upgrades into a `TypeError` on the
   * next install — and this is the ONE break the warn-once shims below cannot catch,
   * because the property still exists and still returns an object; it is simply a
   * different object. TS consumers get a compile error (1.6 typed this `any`); vanilla
   * JS and CDN consumers — the audience this component's own docs court — would get the
   * bare TypeError with no explanation at all.
   *
   * So: one neutral note on first access. `editor` is NOT deprecated — it is the
   * supported CM6 accessor — so this is `console.info` (not `warn`) and leads with that,
   * to help a 1.6→1.7 migrator without scolding correct CM6 use. Use
   * `undo()`/`redo()`/`canUndo()`/`canRedo()` for history — they survived the migration.
   */
  get editor(): CmHandle['view'] | undefined {
    if (!warnedEditor) {
      warnedEditor = true
      console.info(
        '<tosi-code>.editor is the CodeMirror 6 EditorView (the supported accessor; it ' +
          'was an ACE editor in 1.6). ACE-era `editor.session` / `getSession()` / ' +
          '`setOption()` are gone — use undo()/redo()/canUndo()/canRedo() for history.'
      )
    }
    return this._handle?.view
  }

  // ── Removed in 1.7 (ACE → CodeMirror 6) ────────────────────────────────────
  // `^1.6.x` resolves 1.7.0, so existing consumers auto-upgrade into this. Left as
  // warn-once no-ops rather than simply deleted, so `theme`/`options`/`ace` fail
  // with an actionable message instead of silently doing nothing (or, for `ace`,
  // a bare `TypeError` on undefined). See CHANGELOG 1.7.0 → Breaking.

  /** @deprecated Removed in 1.7 — CodeMirror themes via `--code-bg`/`--text-color`. */
  get theme(): string {
    warnRemoved(
      'theme',
      'style the editor with --code-bg / --text-color instead'
    )
    return ''
  }
  set theme(_: string) {
    warnRemoved(
      'theme',
      'style the editor with --code-bg / --text-color instead'
    )
  }

  /** @deprecated Removed in 1.7 — ACE-shaped options have no CodeMirror equivalent. */
  get options(): Record<string, unknown> {
    warnRemoved(
      'options',
      'configure CodeMirror via the `editor` (EditorView) instead'
    )
    return {}
  }
  set options(_: Record<string, unknown>) {
    warnRemoved(
      'options',
      'configure CodeMirror via the `editor` (EditorView) instead'
    )
  }

  /** @deprecated Removed in 1.7 — there is no ACE global; use `editor` (an EditorView). */
  get ace(): undefined {
    warnRemoved('ace', 'use `editor`, which is now a CodeMirror EditorView')
    return undefined
  }

  // History control — so consumers use these instead of reaching into `editor`.
  undo(): void {
    this._handle?.undo()
  }
  redo(): void {
    this._handle?.redo()
  }
  canUndo(): boolean {
    return this._handle?.canUndo() ?? false
  }
  canRedo(): boolean {
    return this._handle?.canRedo() ?? false
  }

  // `diffHost` starts hidden — an always-present absolutely-positioned overlay would
  // otherwise sit on top of the editor and swallow every click.
  content = () => [
    div({ part: 'host' }),
    div({ part: 'diffHost', hidden: true }),
  ]

  static shadowStyleSpec = {
    ':host': {
      display: 'block',
      position: 'relative',
      width: '100%',
      height: '100%',
    },
    '[part="host"]': { height: '100%' },
    /*
    The nested `<tosi-diff>` inherits the EDITOR's palette, not the page's (#143).

    `<tosi-diff>` resolves its own surface as `--tosi-diff-bg || --background` and its text as
    `--tosi-diff-color || --text-color`. Inside `<tosi-code>`, `--text-color` is deliberately
    the light code colour — so CodeMirror text reads against the dark `--code-bg` — while
    `--background` is still the page's white. The nested diff therefore rendered near-white
    text on a white surface, and its unchanged/context lines were invisible. The one place the
    component mounts a diff ITSELF was the one place the default did not work.

    Only the SURFACE is redefined. The text was already right: `--text-color` inside a code
    editor IS the code colour, which is why the diff's own `--tosi-diff-color || --text-color`
    chain lands correctly and only its background was wrong. (Writing `--text-color` here would
    also have been a self-referential cycle — invalid at computed-value time, so the property
    silently unsets rather than falling back, which looks like a fix and is not.)

    Fixed by redefining what the FALLBACK means inside this subtree rather than by setting
    `--tosi-diff-*` here: the consumer's `--tosi-diff-bg`/`--tosi-diff-color` is still checked
    first and still wins, so the workaround adopters already shipped keeps working. Setting the
    `--tosi-diff-*` properties on this element would have overridden them instead — an inner
    definition beats an inherited one — which is a silent break of exactly the escape hatch
    #143 was filed about.
    */
    '[part="diffHost"]': {
      position: 'absolute',
      inset: '0',
      zIndex: '5',
      overflow: 'auto',
      _background: varDefault.codeBg(varDefault.inputBg('#fdfdfd')),
      background: varDefault.tosiDiffBg(varDefault.codeBg('#fdfdfd')),
    },
    '.cm-editor': { height: '100%' },
    '.cm-scroller': {
      outline: 'none',
      fontFamily: "Menlo, Monaco, Consolas, 'Courier New', monospace",
    },
  }

  handleResize() {
    this._handle?.refresh()
  }

  connectedCallback() {
    super.connectedCallback()
    // super.connectedCallback() hydrated us, so `this.hydrated` is now true and
    // `this.parts` is safe from here on.

    if (this.source === '') {
      this.value = this.textContent !== null ? this.textContent.trim() : ''
    }

    // Replay a showDiff() that arrived before we were hydrated.
    if (this._pendingDiff !== undefined) {
      const pending = this._pendingDiff
      this._pendingDiff = undefined
      this.showDiff(pending)
    }

    if (this._loadPromise === undefined) {
      // Lazy chunk — CodeMirror only enters the bundle here, on first editor use.
      const generation = ++this._loadGeneration
      this._loadPromise = import('./code-editor-cm.js').then(
        ({ createCmEditor }) => {
          // The chunk fetch is async, and the pre-load window is WIDE on a cold fetch —
          // doc-browser navigation and closeEditor() both remove editors mid-flight. If we
          // mounted regardless:
          //   append → remove          → an EditorView built into a detached shadow root,
          //                              with no disconnectedCallback left to destroy it.
          //   append → remove → append → TWO views in the host; `_handle` points only at
          //                              the second, so the first (and its darkmode
          //                              listener) is retained forever.
          // That is precisely the leak disconnectedCallback exists to prevent, relocated
          // into the load window. Bail if a newer connect superseded us, or we're detached.
          if (generation !== this._loadGeneration || !this.isConnected)
            return undefined
          const handle = createCmEditor(this.parts.host, {
            value: this.source,
            mode: this.mode,
            readOnly: this.disabled,
            root: this.shadowRoot ?? undefined,
            // The editable area is what a screen reader reaches; the host's name is its name.
            label:
              this.getAttribute('aria-label') ||
              this.title ||
              (this.mode ? `${this.mode} code` : ''),
            onChange: (value) =>
              this.dispatchEvent(
                new CustomEvent('change', { detail: { value } })
              ),
          })
          this._handle = handle
          this._appliedMode = this.mode
          this._appliedDisabled = this.disabled
          this.applyTjsExtension()
          return handle
        }
      )
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    // Without this, `CmHandle.destroy()` has no call sites: the EditorView (and its
    // darkmode listener) outlive the element, so every doc-page navigation leaked all
    // ~20 editors on the page.
    if (this._handle) {
      // Preserve the live text first — `value` falls back to `source` once the handle
      // is gone, and `source` would otherwise hold a stale pre-edit value.
      this.source = this._handle.getValue()
      this._handle.destroy()
      this._handle = undefined
    }
    // Re-connecting rebuilds the editor (a destroyed view can't be reused).
    this._loadPromise = undefined
    this._appliedMode = ''
    this._appliedDisabled = undefined
    // NB: do NOT clear `diffOverlay` — hydrate() runs once, so the shadow DOM (and the
    // overlay inside `diffHost`) survives disconnect. Clearing it would append a second
    // overlay on reconnect.
  }

  private isTjsMode(): boolean {
    return this.mode === 'tjs' || this.mode === 'ajs'
  }

  /**
   * When in tjs mode, lazily upgrade the editor to tjs-lang's CodeMirror language +
   * autocomplete. No-op (keeps TS highlighting) if not tjs, if tjs-lang isn't
   * installed, or if the mode/handle changed before the async load resolved.
   */
  private applyTjsExtension(): void {
    const handle = this._handle
    if (!handle || !this.isTjsMode()) return
    import('./code-editor-cm.js')
      .then(({ loadTjsExtension }) =>
        loadTjsExtension(this._tjsAutocomplete ?? {})
      )
      .then((ext) => {
        if (ext && this._handle === handle && this.isTjsMode()) {
          handle.setLanguageExtension(ext)
          this._tjsExtensionApplied = true
          return
        }
        // Degrade LOUDLY. `loadTjsExtension` swallows everything and returns null by
        // design, and the guard is `typeof mod.tjsEditorExtension === 'function'` — so
        // an upstream export rename returns null without even throwing, and every tjs
        // editor on the page silently falls back to plain TypeScript highlighting with
        // no autocomplete. Nothing goes red; the feature just quietly isn't there.
        if (!ext && this._handle === handle && this.isTjsMode() && !warnedTjs) {
          warnedTjs = true
          console.warn(
            `<tosi-code mode="${this.mode}">: tjs-lang's CodeMirror extension did not load — ` +
              `falling back to TypeScript highlighting (no tjs autocomplete). Install the ` +
              `optional peer \`tjs-lang\`, or check that tjs-lang/editors/codemirror still ` +
              `exports \`tjsEditorExtension\`.`
          )
        }
      })
      .catch((e) => {
        // The chain had no .catch(), so any throw here was an unhandled rejection.
        if (!warnedTjs) {
          warnedTjs = true
          console.warn(`<tosi-code>: failed to apply the tjs extension —`, e)
        }
      })
  }

  /** True once tjs-lang's CM language+autocomplete is actually live (test seam). */
  get tjsExtensionApplied(): boolean {
    return this._tjsExtensionApplied
  }
  private _tjsExtensionApplied = false

  render(): void {
    super.render()

    if (this._handle) {
      if (this.disabled !== this._appliedDisabled) {
        this._handle.setReadOnly(this.disabled)
        this._appliedDisabled = this.disabled
      }
      if (this.mode !== this._appliedMode) {
        this._handle.setMode(this.mode)
        this._appliedMode = this.mode
        this.applyTjsExtension()
      }
    }
  }
}

export const codeEditor =
  CodeEditor.elementCreator() as ElementCreator<CodeEditor>
